import { useState, useEffect } from 'react';
import supabase from '../supabase';

const bg = '#f0ebe3';
const red = '#6B1A1A';
const dark = '#4a1010';
const serif = 'Playfair Display, serif';
const body = 'Cormorant Garamond, serif';

function Onboarding() {
  const contractId = new URLSearchParams(window.location.search).get('id');
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [form, setForm] = useState({
    hours_weekday: '',
    hours_weekend: '',
    services: '',
    no_services: '',
    free_estimates: '',
    service_area: '',
    common_questions: '',
    special_instructions: '',
    transfer_number: '',
    ai_name: '',
    tone: '',
  });

  useEffect(() => {
    if (!contractId) return;
    fetch(`https://api.hermespartnercapital.co/api/contract/${contractId}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setBusinessName(data.contract.prospectInfo.businessName);
      });
  }, [contractId]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    await supabase.from('onboarding').insert({
      contract_id: contractId,
      business_name: businessName,
      hours: { weekday: form.hours_weekday, weekend: form.hours_weekend },
      services: form.services.split(',').map(s => s.trim()),
      emergency_handling: form.tone,
      tone: form.tone,
      transfer_number: form.transfer_number,
      special_instructions: form.special_instructions,
      status: 'client_submitted'
    });
    setSubmitted(true);
  };

  const inputStyle = { backgroundColor: 'transparent', border: 'none', borderBottom: '0.5px solid #d4c9bc', padding: '12px 0', fontSize: '15px', fontFamily: body, color: '#2a1a1a', outline: 'none', width: '100%' };
  const labelStyle = { fontSize: '11px', letterSpacing: '2px', color: '#888', display: 'block', marginBottom: '8px' };
  const fieldStyle = { marginBottom: '32px' };

  if (submitted) return (
    <div style={{ backgroundColor: bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: body }}>
      <div style={{ textAlign: 'center', maxWidth: '500px', padding: '60px 40px' }}>
        <p style={{ fontSize: '48px', marginBottom: '24px' }}>🦅</p>
        <h2 style={{ fontFamily: serif, fontSize: '36px', color: dark, marginBottom: '16px' }}>You're All Set</h2>
        <p style={{ color: '#888', fontSize: '16px', lineHeight: '1.8' }}>We've received your information. Our team will have your AI receptionist live within 5 business days. We'll email you your login credentials once everything is ready.</p>
      </div>
    </div>
  );

  return (
    <div style={{ backgroundColor: bg, minHeight: '100vh', fontFamily: body }}>
      <div style={{ backgroundColor: red, padding: '24px 40px', textAlign: 'center' }}>
        <p style={{ color: '#c9a882', fontSize: '11px', letterSpacing: '4px', margin: '0 0 8px' }}>HERMES PARTNER CAPITAL</p>
        <h1 style={{ fontFamily: serif, color: 'white', fontSize: '28px', margin: 0 }}>Welcome, {businessName}</h1>
        <p style={{ color: '#f0e0e0', margin: '8px 0 0', fontSize: '14px' }}>Help us customize your AI receptionist — takes about 5 minutes</p>
      </div>

      {/* Progress */}
      <div style={{ display: 'flex', gap: '4px' }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ flex: 1, height: '4px', backgroundColor: i <= step ? red : '#d4c9bc', transition: 'background 0.3s' }} />
        ))}
      </div>

      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '60px 40px' }}>

        {/* Step 1 — Business Hours & Services */}
        {step === 1 && (
          <div>
            <p style={{ color: red, fontSize: '11px', letterSpacing: '3px', margin: '0 0 8px' }}>STEP 1 OF 3</p>
            <h2 style={{ fontFamily: serif, fontSize: '32px', color: dark, margin: '0 0 40px' }}>Your Business</h2>

            <div style={fieldStyle}>
              <label style={labelStyle}>WEEKDAY HOURS</label>
              <input style={inputStyle} value={form.hours_weekday} onChange={e => update('hours_weekday', e.target.value)} placeholder="e.g. Monday–Friday, 8:00am – 6:00pm" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>WEEKEND HOURS (leave blank if closed)</label>
              <input style={inputStyle} value={form.hours_weekend} onChange={e => update('hours_weekend', e.target.value)} placeholder="e.g. Saturday 9am–2pm, Sunday closed" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>ALL SERVICES YOU OFFER (comma separated)</label>
              <textarea rows={3} style={{ ...inputStyle, resize: 'none' }} value={form.services} onChange={e => update('services', e.target.value)} placeholder="e.g. Drain cleaning, water heater install, pipe repair, leak detection" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>SERVICES YOU DO NOT OFFER</label>
              <input style={inputStyle} value={form.no_services} onChange={e => update('no_services', e.target.value)} placeholder="e.g. Septic, gas lines, pool plumbing" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>DO YOU OFFER FREE ESTIMATES?</label>
              <select style={{ ...inputStyle, backgroundColor: 'transparent' }} value={form.free_estimates} onChange={e => update('free_estimates', e.target.value)}>
                <option value="">Select...</option>
                <option value="yes">Yes — free estimates</option>
                <option value="no">No — paid estimates</option>
                <option value="depends">Depends on the job</option>
              </select>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>SERVICE AREA (cities or zip codes you cover)</label>
              <input style={inputStyle} value={form.service_area} onChange={e => update('service_area', e.target.value)} placeholder="e.g. Chicago, Naperville, Schaumburg, Palatine" />
            </div>

            <button onClick={() => setStep(2)} style={{ backgroundColor: red, color: bg, padding: '16px 40px', fontSize: '11px', letterSpacing: '3px', border: 'none', cursor: 'pointer', fontFamily: body }}>
              CONTINUE
            </button>
          </div>
        )}

        {/* Step 2 — Call Handling */}
        {step === 2 && (
          <div>
            <p style={{ color: red, fontSize: '11px', letterSpacing: '3px', margin: '0 0 8px' }}>STEP 2 OF 3</p>
            <h2 style={{ fontFamily: serif, fontSize: '32px', color: dark, margin: '0 0 40px' }}>Call Handling</h2>

            <div style={fieldStyle}>
              <label style={labelStyle}>PHONE NUMBER TO TRANSFER CALLS TO (if caller insists on speaking to someone)</label>
              <input style={inputStyle} value={form.transfer_number} onChange={e => update('transfer_number', e.target.value)} placeholder="e.g. (224) 475-9646" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>COMMON QUESTIONS CALLERS ASK + HOW TO ANSWER THEM</label>
              <textarea rows={5} style={{ ...inputStyle, resize: 'none' }} value={form.common_questions} onChange={e => update('common_questions', e.target.value)} placeholder={`e.g.\nQ: Do you offer free estimates? A: Yes, we offer free on-site estimates.\nQ: How soon can you come out? A: We can usually be there same day or next day.\nQ: Are you licensed and insured? A: Yes, fully licensed and insured.`} />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setStep(1)} style={{ backgroundColor: 'transparent', border: '0.5px solid #d4c9bc', padding: '16px 40px', fontSize: '11px', letterSpacing: '3px', fontFamily: body, cursor: 'pointer', color: '#555' }}>
                BACK
              </button>
              <button onClick={() => setStep(3)} style={{ backgroundColor: red, color: bg, padding: '16px 40px', fontSize: '11px', letterSpacing: '3px', border: 'none', cursor: 'pointer', fontFamily: body }}>
                CONTINUE
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — AI Personality */}
        {step === 3 && (
          <div>
            <p style={{ color: red, fontSize: '11px', letterSpacing: '3px', margin: '0 0 8px' }}>STEP 3 OF 3</p>
            <h2 style={{ fontFamily: serif, fontSize: '32px', color: dark, margin: '0 0 40px' }}>Your AI Receptionist</h2>

            <div style={fieldStyle}>
              <label style={labelStyle}>WHAT SHOULD THE AI'S NAME BE?</label>
              <input style={inputStyle} value={form.ai_name} onChange={e => update('ai_name', e.target.value)} placeholder="e.g. Sarah, Alex, or leave blank to use your business name" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>PREFERRED TONE</label>
              <select style={{ ...inputStyle, backgroundColor: 'transparent' }} value={form.tone} onChange={e => update('tone', e.target.value)}>
                <option value="">Select...</option>
                <option value="friendly_casual">Friendly and casual</option>
                <option value="professional_formal">Professional and formal</option>
                <option value="warm_reassuring">Warm and reassuring</option>
                <option value="confident_direct">Confident and direct</option>
              </select>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>ANYTHING THE AI SHOULD ALWAYS SAY OR NEVER SAY?</label>
              <textarea rows={4} style={{ ...inputStyle, resize: 'none' }} value={form.special_instructions} onChange={e => update('special_instructions', e.target.value)} placeholder="e.g. Always mention we're licensed and insured. Never quote prices over the phone. Always offer to schedule a free estimate." />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setStep(2)} style={{ backgroundColor: 'transparent', border: '0.5px solid #d4c9bc', padding: '16px 40px', fontSize: '11px', letterSpacing: '3px', fontFamily: body, cursor: 'pointer', color: '#555' }}>
                BACK
              </button>
              <button onClick={handleSubmit} style={{ backgroundColor: red, color: bg, padding: '16px 40px', fontSize: '11px', letterSpacing: '3px', border: 'none', cursor: 'pointer', fontFamily: body }}>
                SUBMIT
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Onboarding;